/**
 * Filing-details GET returns calendar dates as DD-MM-YYYY.
 * Forms and PATCH payloads expect YYYY-MM-DD (ISO date) for HTML date inputs and backend validation.
 */

function parsePartsToDate(day: number, month: number, year: number): Date | null {
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) return null;
  const d = new Date(year, month - 1, day);
  return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day
    ? d
    : null;
}

function isAllDigits(s: string): boolean {
  if (!s) return false;
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    if (c < 48 || c > 57) return false;
  }
  return true;
}

/** Parse DD-MM-YYYY or YYYY-MM-DD into a local calendar Date, or null if invalid. */
export function parseFilingApiDateString(s: string | null | undefined): Date | null {
  if (s == null) return null;
  const t = String(s).trim();
  if (!t) return null;
  const parts = t.split("-");
  if (parts.length === 3 && parts.every(isAllDigits)) {
    const [a, b, c] = parts.map((p) => parseInt(p, 10));
    if (parts[0].length === 4) {
      // YYYY-MM-DD
      return parsePartsToDate(c, b, a);
    }
    if (parts[2].length === 4) {
      // DD-MM-YYYY
      return parsePartsToDate(a, b, c);
    }
  }
  const fallback = new Date(t);
  return isNaN(fallback.getTime()) ? null : fallback;
}

/**
 * Format a Date as "YYYY-MM-DD" using LOCAL components.
 *
 * Use this when serializing a calendar Date the user picked — `.toISOString()`
 * converts local-midnight to UTC, which shifts the date back one day for users
 * in positive UTC offsets (e.g. IST: a 19-May local-midnight becomes 18-May UTC).
 */
export function toLocalYmd(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Normalize API date string to YYYY-MM-DD for form state and PATCH bodies. */
export function normalizeApiDateToIsoForForm(
  s: string | null | undefined
): string | undefined {
  const d = parseFilingApiDateString(s);
  return d ? toLocalYmd(d) : undefined;
}
